import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { MbcSessionIds } from "../lib/session-ids";
import type { MBCUIMessage } from "../types/mbc-chat.types";

import {
  MBC_CHAT_API,
  MBC_MASTRA_START_MESSAGE,
} from "../config/mbc-chat.config";
import { fetchThreadMessages } from "../lib/messages-api";
import { createEmptyRequestDraft } from "../lib/request-draft-api";
import { mbcSessionIds } from "../lib/session-ids";

type UseMBCChatParams = {
  /**
   * Gate: only start the agent conversation once the brand step is resolved.
   * The brand itself is no longer sent — the AI service resolves it from the
   * request behind the thread — but it still decides *when* the conversation
   * starts.
   */
  enabled: boolean;
  /** Project the request belongs to — required to create it on first send. */
  projectId?: number;
  /** Reopen an existing draft thread instead of starting a fresh one. */
  resume?: MbcSessionIds;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useMBCChat({ enabled, projectId, resume }: UseMBCChatParams) {
  const hasInitialized = useRef(false);
  // Resume: ids are already known (they came from the reopened draft's URL).
  // Fresh conversation: no MySQL request exists yet — creating one here would
  // litter /requests with a draft for every abandoned page open (D1). It's
  // created on the first turn instead (see `ensureSession` below) and the
  // conversation starts on ids derived from the new requestId (D2).
  const [session, setSession] = useState<MbcSessionIds | null>(resume ?? null);
  // Mirrors `session` for synchronous reads inside callbacks — state updates
  // are batched and not visible until the next render, but the single-flight
  // guard below needs the current value the instant creation resolves.
  const sessionRef = useRef(session);

  sessionRef.current = session;

  // Holds the in-flight creation promise so a second caller (a re-render, a
  // double-click) reuses it instead of firing a second `POST /requests` —
  // duplicating that request is exactly the bug this feature removes. Set
  // synchronously inside `ensureSession`, before any await, so it is visible
  // to a second caller invoked in the same tick.
  const creationRef = useRef<null | Promise<MbcSessionIds>>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [creationError, setCreationError] = useState<Error | null>(null);

  const ensureSession = useCallback((): Promise<MbcSessionIds> => {
    if (sessionRef.current) return Promise.resolve(sessionRef.current);
    if (creationRef.current) return creationRef.current;

    setIsCreatingSession(true);
    setCreationError(null);

    const promise = (
      projectId == null
        ? Promise.reject<number>(
            new Error("Cannot start an MBC conversation without a project."),
          )
        : createEmptyRequestDraft(projectId)
    )
      .then((requestId) => {
        const ids = mbcSessionIds(projectId!, requestId);

        sessionRef.current = ids;
        setSession(ids);

        return ids;
      })
      .catch((error_: unknown) => {
        const normalized =
          error_ instanceof Error ? error_ : new Error(String(error_));

        setCreationError(normalized);
        throw normalized;
      })
      .finally(() => {
        setIsCreatingSession(false);
        creationRef.current = null;
      });

    creationRef.current = promise;

    return promise;
  }, [projectId]);

  // Bumped whenever a turn finishes streaming, so the live brief-form panel
  // refetches the persisted working memory.
  const [refreshSignal, setRefreshSignal] = useState(0);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: MBC_CHAT_API,
        // Mastra reads thread/resource from the request body to scope working
        // memory. Nothing else goes in: the agent's grounding (brand, design
        // type) is resolved server-side from the request behind the thread, so
        // a customer cannot ground their brief on a brand they do not have
        // access to. The memory ids here are only a fallback default — every
        // real send overrides them explicitly (see `startTurn`) with ids
        // resolved at send time, so a stale/empty default here can never reach
        // the server.
        body: {
          memory: {
            resource: session?.resourceId ?? "",
            thread: session?.threadId ?? "",
          },
        },
      }),
    [session],
  );

  const {
    error: chatError,
    messages,
    sendMessage,
    setMessages,
    status: chatStatus,
    stop,
  } = useChat<MBCUIMessage>({ transport });

  // Session creation has no useChat status of its own (no message has been
  // sent yet), so it's folded into the status this hook reports — the
  // existing status-driven input lock (MBCChatInput's `isDisabled`) then
  // covers the extra `POST /requests` latency for free, same as a real turn.
  const status = isCreatingSession ? "submitted" : chatStatus;
  const error = creationError ?? chatError;

  // Resuming a draft: load the thread's persisted transcript into the chat once
  // so the conversation history shows instead of an empty chat.
  const hasRehydrated = useRef(false);
  // The thread id in the URL can point at nothing — a discarded session, an
  // expired thread, a stale bookmark. When that happens the chat must start
  // fresh instead of sitting empty with the kickoff suppressed by `resume`.
  const [isResumeEmpty, setIsResumeEmpty] = useState(false);

  useEffect(() => {
    if (!resume || hasRehydrated.current) return;
    hasRehydrated.current = true;

    void fetchThreadMessages(resume.threadId, resume.resourceId)
      .then((history) => {
        if (history.length > 0) setMessages(history);
        else setIsResumeEmpty(true);
      })
      .catch(() => {
        // Non-fatal: fall back to an empty chat the user can continue.
        setIsResumeEmpty(true);
      });
  }, [resume, setMessages]);

  // Single entry point for every turn — the auto kickoff below and manual
  // sends both go through this, so request creation and the send it gates
  // are defined in exactly one place.
  const startTurn = useCallback(
    (text: string) => {
      if (status !== "ready") return;
      // `ensureSession` is single-flight, but that alone still lets two
      // callers each await the same in-flight promise and both reach
      // `sendMessage` once it resolves. `creationRef` is set synchronously
      // (before `ensureSession`'s first await), so checking it here — before
      // this call even starts its own await — stops a second call arriving
      // in the same tick (kickoff racing a fast click).
      if (creationRef.current) return;

      void (async () => {
        let ids: MbcSessionIds;

        try {
          ids = await ensureSession();
        } catch {
          // Surfaced via `creationError` above; no turn is sent — a
          // conversation with no request behind it is the orphan state this
          // feature removes.
          return;
        }

        void sendMessage(
          { text },
          {
            body: {
              memory: { resource: ids.resourceId, thread: ids.threadId },
            },
          },
        );
      })();
    },
    [ensureSession, sendMessage, status],
  );

  useEffect(() => {
    if (!enabled) return;
    // Resuming a draft that actually has history: don't fire the kickoff
    // message — the user just continues typing.
    if (resume && !isResumeEmpty) return;
    if (hasInitialized.current || messages.length > 0) return;
    if (status !== "ready") return;

    hasInitialized.current = true;
    startTurn(MBC_MASTRA_START_MESSAGE);
  }, [enabled, isResumeEmpty, messages.length, resume, startTurn, status]);

  // Detect end of a turn (streaming -> ready) to trigger a memory refetch.
  const prevStatus = useRef(status);

  useEffect(() => {
    const wasStreaming =
      prevStatus.current === "streaming" || prevStatus.current === "submitted";

    if (wasStreaming && status === "ready") {
      setRefreshSignal((n) => n + 1);
    }
    prevStatus.current = status;
  }, [status]);

  const isStreaming = status === "streaming" || status === "submitted";

  return {
    error,
    /** Resume pointed at a thread with no history (discarded/expired/stale). */
    isResumeEmpty,
    isStreaming,
    messages,
    refreshSignal,
    resourceId: session?.resourceId,
    sendUserMessage: startTurn,
    status,
    stop,
    threadId: session?.threadId,
  };
}
